import React from "react";
import { formatTimestamp } from "src/utils/dateHandler.util";
import { Amount } from "src/components/Amount";

import {
  FiHash as Hash,
  FiCalendar as Calendar,
  FiClock as Clock,
  FiGlobe as Globe,
  FiFlag as Flag,
  FiUser as Users,
  FiDollarSign as DollarSign,
  FiLink as Link,
} from "react-icons/fi";
import { LoungeWithUsers } from "../../types";
import { LoungeHeader } from "./header";
import { LoungeInfoItem } from "./info-item";
import { LoungeParticipants } from "./lounge-participants";
import { QuickStats } from "./quick-stats";
import { StatusIcon } from "./status-icon";

export const LoungeInfo: React.FC<{ data: LoungeWithUsers }> = ({ data }) => {
  const { lounge } = data;
  const totalJoiners = Object.values(data.joiners ?? {}).filter(Boolean).length;
  const selectedCount = totalJoiners ? Object.values(data.joiners ?? {}).filter((j) => j?.isSelected).length : 0;

  return (
    <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-xl">
      <LoungeHeader />

      <div className="p-8 space-y-10">
        <section className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Hash className="w-5 h-5 text-blue-600" />
            Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <LoungeInfoItem icon={Hash} label="Lounge ID" text={lounge.id} />
              <LoungeInfoItem icon={Users} label="Initiator Type" text={lounge.initiatorType} />
              <LoungeInfoItem icon={Globe} label="Language" text={lounge.langCode} />
            </div>

            <div className="space-y-4">
              <LoungeInfoItem icon={Calendar} label="Created At" text={formatTimestamp(lounge.createdAt)} />
              <LoungeInfoItem icon={Clock} label="Expiry Date" text={formatTimestamp(lounge.expiryDate)} />
              <div className="flex items-start space-x-4 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200">
                <div className="p-2 rounded-lg bg-emerald-100 shadow-sm">
                  <StatusIcon status={lounge.status} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Status</p>
                  <div className="px-3 py-1.5 bg-white rounded-lg border border-emerald-200 shadow-sm">
                    <p className="text-sm text-emerald-700 font-bold">{lounge.status}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <LoungeInfoItem
                icon={DollarSign}
                label="Budget"
                text={<Amount amount={lounge.budget} currency={lounge.currency} />}
                highlight={true}
              />
              <LoungeInfoItem icon={Flag} label="Currency" text={lounge.currency} />
              {lounge.sessionId && (
                <LoungeInfoItem
                  icon={Link}
                  label="Linked Session"
                  text={lounge.sessionId}
                  link={"/sessions/" + lounge.sessionId}
                />
              )}
            </div>
          </div>
        </section>

        <LoungeParticipants data={data} />

        <QuickStats selectedCount={selectedCount} totalJoiners={totalJoiners} />
      </div>
    </div>
  );
};
